<template>
  <div class="bg-background min-h-screen">
    <div class="container mx-auto px-4 py-12 max-w-6xl">

      <!-- Header -->
      <div class="text-center mb-6 space-y-3">
        <template v-if="isOnFreePlan">
          <h1 class="text-3xl sm:text-4xl font-semibold tracking-tight">
            You're on the Free plan.
          </h1>
          <p class="text-muted-foreground max-w-xl mx-auto">
            Upgrade for more AI cover letters, instant alerts and Auto Apply.
          </p>
        </template>
        <template v-else-if="currentPlan && currentPlan.status === 'active'">
          <h1 class="text-3xl sm:text-4xl font-semibold tracking-tight">
            Manage your subscription.
          </h1>
          <p class="text-muted-foreground max-w-xl mx-auto">
            Switch plans anytime — changes apply right away.
          </p>
        </template>
        <template v-else-if="currentPlan && currentPlan.status === 'expired'">
          <h1 class="text-3xl sm:text-4xl font-semibold tracking-tight">
            Your plan has ended.
          </h1>
          <p class="text-muted-foreground max-w-xl mx-auto">
            You can keep using Applypa on the Free plan, or resubscribe for higher limits.
          </p>
        </template>
        <template v-else>
          <h1 class="text-3xl sm:text-4xl font-semibold tracking-tight">
            Try full Premium free for 14 days.
          </h1>
          <p class="text-muted-foreground max-w-xl mx-auto">
            After the trial you stay on the Free plan, or pick the plan that fits.
          </p>
        </template>

        <!-- Billing Period Tabs -->
        <div class="flex justify-center pt-2">
          <div class="inline-flex bg-muted rounded-xl p-1 gap-1">
            <button
              v-for="tab in periodTabs"
              :key="tab.value"
              @click="selectedPeriod = tab.value"
              :class="[
                'relative flex items-center gap-1.5 rounded-lg px-4 py-2 text-sm transition-all duration-200',
                selectedPeriod === tab.value
                  ? 'bg-background text-foreground shadow-sm ring-1 ring-border/60 font-semibold'
                  : 'font-medium text-muted-foreground hover:text-foreground hover:bg-background/50'
              ]"
            >
              {{ tab.label }}
              <span
                v-if="tab.badge"
                :class="[
                  'rounded-full px-1.5 py-0.5 text-[10px] font-bold leading-none tracking-wide transition-all duration-200',
                  selectedPeriod === tab.value
                    ? 'bg-green-500 text-white shadow-sm shadow-green-500/40'
                    : 'bg-green-500/15 text-green-700 dark:text-green-300'
                ]"
              >
                {{ tab.badge }}
              </span>
            </button>
          </div>
        </div>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="flex justify-center py-16">
        <div class="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent"></div>
      </div>

      <!-- Error -->
      <div v-else-if="error" class="rounded-xl border border-destructive/20 bg-destructive/5 p-4 text-sm text-destructive max-w-lg mx-auto text-center">
        {{ error }}
      </div>

      <div v-else>
        <!-- Checkout error -->
        <div
          v-if="checkoutError"
          class="rounded-xl border border-destructive/20 bg-destructive/5 p-4 text-sm text-destructive max-w-2xl mx-auto mb-8 text-center"
        >
          {{ checkoutError }}
        </div>

        <!-- Pricing cards -->
        <div class="grid md:grid-cols-3 gap-6 items-stretch py-6">

          <!-- Free Plan (the plan you move to after the 14-day Premium trial) -->
          <div class="rounded-xl border border-border bg-card shadow-sm flex flex-col">
            <div class="p-6 flex flex-col flex-1">
              <div class="mb-5">
                <div class="inline-flex h-10 w-10 items-center justify-center rounded-full bg-muted ring-4 ring-muted/50 mb-3">
                  <i class="fas fa-seedling text-muted-foreground text-lg"></i>
                </div>
                <h3 class="text-xl font-semibold">Free</h3>
                <p class="text-sm text-muted-foreground mt-1">Where you land after your 14-day Premium trial</p>
              </div>

              <div class="mb-6">
                <div class="flex items-baseline gap-1">
                  <span class="text-4xl font-bold">{{ formatPeso(0) }}</span>
                </div>
                <div class="mt-1 text-sm text-muted-foreground">No payment needed</div>
              </div>

              <ul class="space-y-2.5 flex-1 mb-6">
                <li v-for="row in freeFeatures" :key="row.label" class="flex items-start gap-2.5 text-sm">
                  <template v-if="row.type === 'value' || row.enabled">
                    <span class="inline-flex h-4 w-4 shrink-0 mt-0.5 items-center justify-center rounded-full bg-foreground/80 text-background">
                      <i class="fas fa-check text-[8px]"></i>
                    </span>
                    <span>{{ row.type === 'value' ? row.text : row.label }}</span>
                  </template>
                </li>
              </ul>

              <div
                v-if="isOnFreePlan"
                class="rounded-lg bg-muted border border-border px-3 py-2 text-sm text-foreground text-center font-medium"
              >
                <i class="fas fa-check-circle mr-1.5"></i> Current plan
              </div>
              <p v-else class="text-xs text-muted-foreground text-center">
                Starts automatically when your trial or plan ends.
              </p>
            </div>
          </div>

          <!-- Basic Plan -->
          <div class="rounded-xl border border-primary/30 bg-card shadow-md hover:shadow-lg transition-shadow duration-200 flex flex-col" style="background: linear-gradient(160deg, var(--card) 0%, hsl(var(--primary)/0.04) 100%);">
            <div class="p-6 flex flex-col flex-1">
              <div class="mb-5">
                <div class="inline-flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 ring-4 ring-primary/5 mb-3">
                  <i class="fas fa-star text-primary text-lg"></i>
                </div>
                <h3 class="text-xl font-semibold">Basic</h3>
                <p class="text-sm text-muted-foreground mt-1">For freelancers who want to apply faster</p>
              </div>

              <div class="mb-6">
                <div v-if="basicPlan">
                  <div class="flex items-baseline gap-1">
                    <span class="text-4xl font-bold text-primary">{{ formatPeso(monthlyPrice(basicPlan.price)) }}</span>
                    <span class="text-muted-foreground text-sm">/month</span>
                  </div>
                  <div v-if="selectedPeriod !== 'monthly'" class="mt-1 text-sm text-muted-foreground">
                    {{ formatPeso(basicPlan.price) }} paid {{ periodTotalLabel }}
                  </div>
                  <div v-if="selectedPeriod !== 'monthly' && savingsAmount(basicPlan, basicMonthlyPlan)" class="mt-2">
                    <span class="inline-flex items-center gap-1 rounded-full bg-green-500 text-white px-2.5 py-0.5 text-xs font-bold">
                      <i class="fas fa-piggy-bank text-[9px]"></i>
                      You save {{ formatPeso(savingsAmount(basicPlan, basicMonthlyPlan)) }}
                    </span>
                  </div>
                </div>
                <div v-else class="text-sm text-muted-foreground italic">Not available</div>
              </div>

              <!-- Feature matrix: Basic -->
              <ul class="space-y-2.5 flex-1 mb-6">
                <li v-for="row in basicFeatures" :key="row.label" class="flex items-start gap-2.5 text-sm">
                  <template v-if="row.type === 'value'">
                    <span class="inline-flex h-4 w-4 shrink-0 mt-0.5 items-center justify-center rounded-full bg-primary text-primary-foreground">
                      <i class="fas fa-check text-[8px]"></i>
                    </span>
                    <span>{{ row.text }}</span>
                  </template>
                  <template v-else-if="row.enabled">
                    <span class="inline-flex h-4 w-4 shrink-0 mt-0.5 items-center justify-center rounded-full bg-primary text-primary-foreground">
                      <i class="fas fa-check text-[8px]"></i>
                    </span>
                    <span>{{ row.label }}</span>
                  </template>
                </li>
              </ul>

              <div class="space-y-2">
                <div
                  v-if="currentPlan && currentPlan.plan && currentPlan.plan.plan_type === 'basic' && currentPlan.status === 'active'"
                  class="rounded-lg bg-primary/10 border border-primary/20 px-3 py-2 text-sm text-primary text-center font-medium"
                >
                  <i class="fas fa-check-circle mr-1.5"></i> Current plan
                </div>
                <div
                  v-else-if="currentPlan && currentPlan.plan && currentPlan.plan.plan_type === 'basic' && currentPlan.status === 'expired'"
                  class="rounded-lg bg-red-500/10 border border-red-500/30 px-3 py-2 text-sm text-red-700 dark:text-red-300 text-center"
                >
                  <i class="fas fa-exclamation-circle mr-1.5"></i> Plan expired
                </div>
                <button
                  v-if="!isActivePaidPlan('basic')"
                  @click="selectPlan('basic')"
                  :disabled="!basicPlan || checkoutLoading || (currentPlan && currentPlan.plan && currentPlan.plan.plan_type === 'basic' && currentPlan.status === 'active')"
                  class="inline-flex w-full items-center justify-center gap-2 rounded-lg border border-primary bg-primary hover:bg-primary/90 text-primary-foreground px-4 py-2.5 text-sm font-semibold transition-colors disabled:opacity-50 disabled:pointer-events-none"
                >
                  <i v-if="checkoutLoading === 'basic'" class="fas fa-spinner fa-spin"></i>
                  {{ checkoutLoading === 'basic' ? 'Redirecting…' : (currentPlan && currentPlan.plan && currentPlan.plan.plan_type === 'basic' && currentPlan.status === 'active' ? 'Current Plan' : (currentPlan && currentPlan.plan && currentPlan.plan.plan_type === 'basic' && currentPlan.status === 'expired' ? 'Resubscribe' : 'Start Applying Now')) }}
                </button>
              </div>
            </div>
          </div>

          <!-- Premium Plan (featured) -->
          <div class="rounded-2xl bg-card border-2 border-violet-500 shadow-2xl shadow-violet-500/25 flex flex-col relative z-10 md:scale-[1.03]" style="background: linear-gradient(160deg, var(--card) 0%, hsl(270 60% 12%) 100%);">
            <!-- Decorative top accent bar -->
            <div class="h-1.5 w-full rounded-t-2xl bg-violet-500"></div>
            <div class="absolute -top-3.5 left-1/2 -translate-x-1/2 whitespace-nowrap">
              <span class="inline-flex items-center gap-1 rounded-full bg-violet-500 text-white px-3 py-1 text-xs font-bold shadow-md tracking-wide uppercase">
                <i class="fas fa-star text-white text-[9px]"></i> Most Popular
              </span>
            </div>
            <div class="p-6 flex flex-col flex-1">
              <div class="mb-5">
                <div class="inline-flex h-10 w-10 items-center justify-center rounded-full bg-violet-500/15 ring-4 ring-violet-500/10 mb-3">
                  <i class="fas fa-crown text-violet-600 dark:text-violet-400 text-lg"></i>
                </div>
                <h3 class="text-xl font-semibold">Premium</h3>
                <p class="text-sm text-muted-foreground mt-1">For serious freelancers who want an edge</p>
              </div>

              <div class="mb-6">
                <div v-if="premiumPlan">
                  <div class="flex items-baseline gap-1">
                    <span class="text-4xl font-bold text-violet-600 dark:text-violet-400">{{ formatPeso(monthlyPrice(premiumPlan.price)) }}</span>
                    <span class="text-muted-foreground text-sm">/month</span>
                  </div>
                  <div v-if="selectedPeriod !== 'monthly'" class="mt-1 text-sm text-muted-foreground">
                    {{ formatPeso(premiumPlan.price) }} paid {{ periodTotalLabel }}
                  </div>
                  <div v-if="selectedPeriod !== 'monthly' && savingsAmount(premiumPlan, premiumMonthlyPlan)" class="mt-2">
                    <span class="inline-flex items-center gap-1 rounded-full bg-green-500 text-white px-2.5 py-0.5 text-xs font-bold">
                      <i class="fas fa-piggy-bank text-[9px]"></i>
                      You save {{ formatPeso(savingsAmount(premiumPlan, premiumMonthlyPlan)) }}
                    </span>
                  </div>
                  <div v-if="selectedPeriod === 'yearly'" class="mt-2">
                    <span class="inline-flex items-center rounded-full bg-green-500/15 text-green-700 dark:text-green-300 px-2.5 py-0.5 text-[11px] font-bold tracking-wide">
                      Best Value
                    </span>
                  </div>
                </div>
                <div v-else class="text-sm text-muted-foreground italic">Not available</div>
              </div>

              <!-- Feature matrix: Premium -->
              <ul class="space-y-2.5 flex-1 mb-6">
                <li v-for="row in premiumFeatures" :key="row.label" class="flex items-start gap-2.5 text-sm">
                  <template v-if="row.type === 'value'">
                    <span class="inline-flex h-4 w-4 shrink-0 mt-0.5 items-center justify-center rounded-full bg-violet-500 text-white">
                      <i class="fas fa-check text-[8px]"></i>
                    </span>
                    <span>{{ row.text }}</span>
                  </template>
                  <template v-else-if="row.enabled">
                    <span class="inline-flex h-4 w-4 shrink-0 mt-0.5 items-center justify-center rounded-full bg-violet-500 text-white">
                      <i class="fas fa-check text-[8px]"></i>
                    </span>
                    <span>{{ row.label }}</span>
                  </template>
                </li>
              </ul>

              <div class="space-y-2">
                <div
                  v-if="currentPlan && currentPlan.plan && currentPlan.plan.plan_type === 'premium' && currentPlan.status === 'active'"
                  class="rounded-lg bg-violet-500/10 border border-violet-500/30 px-3 py-2 text-sm text-violet-700 dark:text-violet-300 text-center font-medium"
                >
                  <i class="fas fa-check-circle mr-1.5"></i> Current plan
                </div>
                <div
                  v-else-if="currentPlan && currentPlan.plan && currentPlan.plan.plan_type === 'premium' && currentPlan.status === 'trial'"
                  class="rounded-lg bg-blue-500/10 border border-blue-500/30 px-3 py-2 text-sm text-blue-700 dark:text-blue-300 text-center font-medium"
                >
                  <i class="fas fa-clock mr-1.5"></i> On free trial
                </div>
                <div
                  v-else-if="currentPlan && currentPlan.plan && currentPlan.plan.plan_type === 'premium' && currentPlan.status === 'expired'"
                  class="rounded-lg bg-red-500/10 border border-red-500/30 px-3 py-2 text-sm text-red-700 dark:text-red-300 text-center"
                >
                  <i class="fas fa-exclamation-circle mr-1.5"></i> Plan expired
                </div>
                <button
                  v-if="!isActivePaidPlan('premium')"
                  @click="selectPlan('premium')"
                  :disabled="!premiumPlan || checkoutLoading || (currentPlan && currentPlan.plan && currentPlan.plan.plan_type === 'premium' && currentPlan.status === 'active')"
                  class="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-violet-600 hover:bg-violet-700 text-white px-4 py-2.5 text-sm font-bold transition-colors shadow-md shadow-violet-500/30 disabled:opacity-50 disabled:pointer-events-none"
                >
                  <i v-if="checkoutLoading === 'premium'" class="fas fa-spinner fa-spin"></i>
                  {{ checkoutLoading === 'premium' ? 'Redirecting…' : (currentPlan && currentPlan.plan && currentPlan.plan.plan_type === 'premium' && currentPlan.status === 'active' ? 'Current Plan' : (currentPlan && currentPlan.plan && currentPlan.plan.plan_type === 'premium' && currentPlan.status === 'expired' ? 'Resubscribe' : (currentPlan && currentPlan.plan && currentPlan.plan.plan_type === 'premium' && currentPlan.status === 'trial' ? 'Subscribe Now' : 'Start Applying Now'))) }}
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Trust badges -->
        <div class="mt-8">
          <div class="border-t border-border/60 pt-6 flex flex-col items-center gap-3">
            <div class="flex flex-wrap items-center justify-center gap-x-5 gap-y-2">
              <div class="flex items-center gap-2 text-sm text-muted-foreground">
                <span class="inline-flex h-5 w-5 items-center justify-center rounded-full bg-primary/10">
                  <i class="fas fa-check text-primary text-[9px]"></i>
                </span>
                <span>No credit card required</span>
              </div>
              <div class="flex items-center gap-2 text-sm text-muted-foreground">
                <span class="inline-flex h-5 w-5 items-center justify-center rounded-full bg-primary/10">
                  <i class="fas fa-check text-primary text-[9px]"></i>
                </span>
                <span>{{ periodPaymentLabel }}</span>
              </div>
              <div class="flex items-center gap-2 text-sm text-muted-foreground">
                <span class="inline-flex h-5 w-5 items-center justify-center rounded-full bg-primary/10">
                  <i class="fas fa-shield-alt text-primary text-[9px]"></i>
                </span>
                <span>Secure checkout via PayMongo</span>
              </div>
            </div>
            <p class="text-xs text-muted-foreground text-center">
              Already landed a job? Just don't renew — no charges, no cancellation needed.
            </p>
          </div>
        </div>

        <!-- Feature Comparison Table -->
        <div class="bg-card rounded-lg shadow-lg p-8 mt-10 mb-6 max-w-5xl mx-auto">
          <h2 class="text-2xl font-bold text-foreground mb-6 text-center">
            Compare Plans & Features
          </h2>
          <div class="overflow-x-auto rounded-lg border border-border">
            <table class="min-w-full text-sm text-foreground">
              <thead>
                <tr class="bg-muted text-foreground border-b border-border">
                  <th class="py-3 px-4 text-left font-semibold">Feature</th>
                  <th class="py-3 px-4 text-center font-semibold">Free</th>
                  <th class="py-3 px-4 text-center font-semibold">Basic</th>
                  <th class="py-3 px-4 text-center font-semibold">Premium</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-border">
                <tr v-for="row in comparisonRows" :key="row.label" class="bg-card even:bg-muted/50">
                  <td class="py-2 px-4">{{ row.label }}</td>
                  <td v-for="col in ['free', 'basic', 'premium']" :key="col" class="py-2 px-4 text-center">
                    <template v-if="row.type === 'bool'">
                      <span v-if="row[col]" class="text-green-600 dark:text-green-400 font-semibold">✓</span>
                      <span v-else class="text-muted-foreground">—</span>
                    </template>
                    <template v-else>{{ row[col] }}</template>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Test Plan (admin only) -->
        <div v-if="isAdmin && testPlan" class="max-w-sm mx-auto mt-10">
          <div class="rounded-xl border-2 border-amber-400 bg-card shadow-sm overflow-hidden relative">
            <div class="absolute -top-3 left-1/2 -translate-x-1/2">
              <span class="rounded-full bg-amber-400 text-amber-900 px-3 py-0.5 text-xs font-semibold shadow">
                Admin Test Plan
              </span>
            </div>
            <div class="p-6">
              <div class="text-center mb-4">
                <div class="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-amber-500/15 mb-3">
                  <i class="fas fa-flask text-amber-600 dark:text-amber-400 text-lg"></i>
                </div>
                <h3 class="text-lg font-semibold">{{ testPlan.name }}</h3>
                <p class="text-xs text-muted-foreground">Admin-only · Premium features</p>
              </div>
              <div class="text-center mb-4">
                <div class="flex items-baseline justify-center gap-1">
                  <span class="text-3xl font-bold text-amber-600 dark:text-amber-400">{{ formatPeso(testPlan.price) }}</span>
                  <span class="text-muted-foreground text-sm">/month</span>
                </div>
              </div>
              <button
                @click="selectTestPlan(testPlan)"
                class="inline-flex w-full items-center justify-center rounded-lg bg-amber-400 hover:bg-amber-500 text-amber-900 px-4 py-2.5 text-sm font-semibold transition-colors"
              >
                Subscribe (Test)
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed } from "vue";
import { useRouter } from "vue-router";
import apiClient from "@/services/api";
import authStore from "@/store/auth";
import {
  PERIOD_DURATION,
  resolvePlanFeatures,
  formatPeso,
  coverLettersCell,
  autoApplyCell,
  savedJobsCell,
  coverLettersLabel,
  autoApplyLabel,
  useSubscriptionPlans,
} from "@/composables/useSubscriptionPlans";

const periodTabs = [
  { value: "monthly", label: "Monthly", badge: null },
  { value: "3months", label: "3 Months", badge: "Save 15%" },
  { value: "yearly", label: "1 Year", badge: "Save 31%" },
];

// A limit row: a check with text when the plan includes it, otherwise a
// "not included" row (limit 0 means not included) that buildFeatureRows drops.
function limitRow(label, text, icon) {
  return text
    ? { type: "value", label, text, icon }
    : { type: "bool", label, enabled: false, icon };
}

// Features a plan doesn't include are left off its list entirely.
function buildFeatureRows(features, planType) {
  const isFree = planType === "free";
  const rows = [
    {
      type: "value",
      label: "Job Filters",
      text: `${features.max_job_filter_presets} Job Filter${features.max_job_filter_presets === 1 ? "" : "s"}`,
      icon: "fas fa-sliders-h",
    },
    limitRow("Auto Apply", autoApplyLabel(features), "fas fa-robot"),
    limitRow("AI Cover Letters", coverLettersLabel(features), "fas fa-pen-fancy"),
    {
      type: "value",
      label: "AI Instructions",
      text: isFree ? "1 AI Instruction" : "Unlimited AI Instructions",
      icon: "fas fa-sliders-h",
    },
    {
      type: "bool",
      label: "Telegram & Discord Alerts",
      enabled: features.has_telegram_alerts,
      icon: "fab fa-telegram-plane",
    },
    {
      type: "value",
      label: "Email Alerts",
      text: isFree ? "Weekly email digest" : "Instant, daily or weekly email alerts",
      enabled: features.has_email_notifications,
      icon: "fas fa-envelope",
    },
    ...(planType === "premium" ? [
      {
        type: "bool",
        label: "Priority Support",
        enabled: features.has_priority_support,
        icon: "fas fa-headset",
      },
      {
        type: "bool",
        label: "Priority Queue",
        enabled: true,
        icon: "fas fa-bolt",
      },
      {
        type: "bool",
        label: "Early Feature Access",
        enabled: true,
        icon: "fas fa-star",
      },
    ] : []),
  ];
  return rows.filter((row) => row.type === "value" || row.enabled);
}

export default {
  name: "SubscriptionPlansPage",
  setup() {
    const router = useRouter();
    const { plans, plansLoading, plansError, fetchSubscriptionPlans, findPlan } = useSubscriptionPlans();
    const checkoutError = ref(null);
    const checkoutLoading = ref(null);
    const currentPlan = ref(null);
    const selectedPeriod = ref("monthly");

    const freePlan = computed(
      () => plans.value.find((p) => p.plan_type === "free" && !p.is_test_plan) || null,
    );
    // On the Free plan, including a lapsed trial/paid plan the expiry job hasn't moved yet.
    const isOnFreePlan = computed(
      () => (currentPlan.value?.effective_plan ?? currentPlan.value?.plan)?.plan_type === "free",
    );
    // The card already shows a "Current plan" badge, so its checkout button is hidden.
    const isActivePaidPlan = (planType) =>
      currentPlan.value?.plan?.plan_type === planType && currentPlan.value?.status === "active";
    const basicPlan = computed(() => findPlan("basic", PERIOD_DURATION[selectedPeriod.value]));
    const premiumPlan = computed(() => findPlan("premium", PERIOD_DURATION[selectedPeriod.value]));

    const testPlan = computed(
      () => plans.value.find((p) => p.is_test_plan) || null,
    );
    const isAdmin = computed(
      () => authStore.state.user?.account_type === "admin",
    );
    const isLoggedIn = computed(() => authStore.state.isLoggedIn);

    // 3-month and yearly plans show what they work out to per month; the
    // full amount paid goes on the line under it.
    const periodTotalLabel = computed(() =>
      selectedPeriod.value === "yearly" ? "once for 1 year" : "once for 3 months",
    );

    const periodPaymentLabel = computed(() => {
      if (selectedPeriod.value === "monthly") return "One-time monthly payment — not a recurring subscription";
      if (selectedPeriod.value === "3months") return "One-time 3-month payment — not a recurring subscription";
      return "One-time yearly payment — not a recurring subscription";
    });

    const monthlyPrice = (price) => {
      if (selectedPeriod.value === "monthly") return price;
      const months = selectedPeriod.value === "yearly" ? 12 : 3;
      return Math.round(Number(price) / months);
    };

    const basicMonthlyPlan = computed(() => findPlan("basic", 30));
    const premiumMonthlyPlan = computed(() => findPlan("premium", 30));

    const savingsAmount = (currentPlan, monthlyPlan) => {
      if (!currentPlan || !monthlyPlan || selectedPeriod.value === "monthly") return null;
      const months = selectedPeriod.value === "yearly" ? 12 : 3;
      return Math.round(Number(monthlyPlan.price) * months - Number(currentPlan.price));
    };

    const freeFeatures = computed(() =>
      buildFeatureRows(resolvePlanFeatures(freePlan.value, "free"), "free"),
    );
    const basicFeatures = computed(() =>
      buildFeatureRows(resolvePlanFeatures(basicPlan.value, "basic"), "basic"),
    );
    const premiumFeatures = computed(() =>
      buildFeatureRows(resolvePlanFeatures(premiumPlan.value, "premium"), "premium"),
    );

    // Feature Comparison Table — driven by the same plan data as the pricing
    // cards above, so it can't drift out of sync with real plan limits.
    const comparisonRows = computed(() => {
      const freeF = resolvePlanFeatures(freePlan.value, "free");
      const basicF = resolvePlanFeatures(basicMonthlyPlan.value, "basic");
      const premiumF = resolvePlanFeatures(premiumMonthlyPlan.value, "premium");
      const each = (fn) => ({ free: fn(freeF), basic: fn(basicF), premium: fn(premiumF) });
      return [
        { label: "AI Cover Letters", type: "text", ...each(coverLettersCell) },
        { label: "Auto Apply", type: "text", ...each(autoApplyCell) },
        { label: "Job Filter Presets", type: "text", ...each((f) => `${f.max_job_filter_presets}`) },
        { label: "Email Alerts", type: "text", free: "Weekly", basic: "Instant, daily, weekly", premium: "Instant, daily, weekly" },
        { label: "Telegram & Discord Alerts", type: "bool", ...each((f) => f.has_telegram_alerts) },
        { label: "Priority Support", type: "bool", ...each((f) => f.has_priority_support) },
        { label: "Saved Jobs", type: "text", ...each(savedJobsCell) },
        { label: "Application Tracker", type: "bool", free: true, basic: true, premium: true },
      ];
    });

    const fetchPlans = async () => {
      checkoutError.value = null;
      await fetchSubscriptionPlans();
      try {
        const subResponse = await apiClient.get("/auth/subscription/current/");
        currentPlan.value = subResponse.data;
      } catch (err) {
        // No current subscription found
      }
    };

    const selectTestPlan = (plan) => {
      checkoutError.value = null;
      const url = (plan.checkout_page_url || "").trim();
      if (!url) {
        checkoutError.value = "No checkout URL set for this test plan. Add one in the admin.";
        return;
      }
      window.location.assign(url);
    };

    const selectPlan = async (planType) => {
      checkoutError.value = null;
      if (!isLoggedIn.value) {
        router.push({ name: "register" });
        return;
      }
      const days = PERIOD_DURATION[selectedPeriod.value];
      const plan = plans.value.find(
        (p) => p.plan_type === planType && !p.is_test_plan && (p.duration_days ?? 30) === days
      );
      if (!plan) {
        checkoutError.value = "This plan is not available. Please try again later.";
        return;
      }
      try {
        checkoutLoading.value = planType;
        const { data } = await apiClient.post("/auth/subscription/checkout-session/", {
          plan_type: planType,
          duration_days: days,
        });
        window.location.assign(data.checkout_url);
      } catch (err) {
        checkoutError.value =
          err.response?.data?.error || "Couldn't start checkout. Please try again or contact support.";
        checkoutLoading.value = null;
      }
    };

    onMounted(async () => {
      await authStore.checkAuthStatus();
      fetchPlans();
    });

    return {
      isActivePaidPlan,
      plans,
      loading: plansLoading,
      error: plansError,
      checkoutError,
      checkoutLoading,
      currentPlan,
      isLoggedIn,
      isAdmin,
      selectedPeriod,
      periodTabs,
      periodTotalLabel,
      periodPaymentLabel,
      monthlyPrice,
      selectPlan,
      selectTestPlan,
      freePlan,
      isOnFreePlan,
      freeFeatures,
      basicPlan,
      premiumPlan,
      testPlan,
      basicFeatures,
      premiumFeatures,
      comparisonRows,
      formatPeso,
      basicMonthlyPlan,
      premiumMonthlyPlan,
      savingsAmount,
    };
  },
};
</script>
